import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import EmailExistsModal from './EmailExistsModal';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const { register, isLoading, error } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return;
    }
    
    // Clear any previous errors
    setLocalError(null);
    
    try {
      const user = await register(formData.email, formData.password, formData.firstName, formData.lastName);
      
      // Clear any error state before redirecting
      setLocalError(null);
      
      // Always redirect based on email verification status
      if (!user.emailVerified) {
        // Immediately redirect to verification page - don't show any error
        navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}`, { replace: true });
      } else {
        // Email already verified, go to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      // Check if this is a 409 conflict (user already exists)
      const errorMsg = err?.message || '';
      const errorLower = errorMsg.toLowerCase();
      if (err?.response?.status === 409 || 
          err?.status === 409 ||
          errorLower.includes('already exists') || 
          errorLower.includes('already registered') ||
          errorLower.includes('email address is already registered')) {
        // Show modal for existing email
        setShowEmailExistsModal(true);
        setLocalError(null);
        return;
      }
      
      // Check if this is an email verification related message
      if (errorMsg.toLowerCase().includes('verification') || errorMsg.toLowerCase().includes('verify') || errorMsg.toLowerCase().includes('activate') || errorMsg.toLowerCase().includes('check your email')) {
        // If it's about email verification, redirect to verification page instead of showing error
        setLocalError(null); // Clear error before redirect
        navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}`, { replace: true });
      } else {
        // Check for 500 server errors
        if (err?.response?.status === 500 || err?.status === 500) {
          setLocalError('Server error. Please try again in a moment, or contact support if the problem persists.');
        } else {
          // Only show actual registration errors (not verification related)
          setLocalError(errorMsg || 'Registration failed. Please try again.');
        }
      }
    }
  };

  const passwordsMatch = formData.password === formData.confirmPassword || formData.confirmPassword === '';

  const handleResendVerification = async () => {
    try {
      const response = await fetch('/api/v1/email/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to resend verification email');
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to resend verification email');
    }
  };

  const handleCloseModal = () => {
    setShowEmailExistsModal(false);
  };

  const handleSwitchToLoginFromModal = () => {
    setShowEmailExistsModal(false);
    onSwitchToLogin();
  };

  return (
    <>
      {showEmailExistsModal && (
        <EmailExistsModal
          email={formData.email}
          onClose={handleCloseModal}
          onResendVerification={handleResendVerification}
          onSwitchToLogin={handleSwitchToLoginFromModal}
        />
      )}
      <div className="card max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Create Account</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium mb-2">
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              className="input"
              placeholder="First name"
              required
            />
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium mb-2">
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              className="input"
              placeholder="Last name"
              required
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className="input"
            placeholder="Enter your email"
            required
          />
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              className="input pr-10"
              placeholder="Create a password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Must contain at least 8 characters with uppercase, lowercase, and number
          </p>
        </div>
        
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`input pr-10 ${formData.confirmPassword && !passwordsMatch ? 'border-red-500' : ''}`}
              placeholder="Confirm your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {formData.confirmPassword && !passwordsMatch && (
            <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
          )}
        </div>
        
        {(localError || (error && !error.toLowerCase().includes('verification') && !error.toLowerCase().includes('verify') && !error.toLowerCase().includes('activate'))) && (
          <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            {localError || error}
          </div>
        )}
        
        <button
          type="submit"
          disabled={isLoading || !passwordsMatch}
          className="btn primary w-full"
        >
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-cyan-400 hover:text-cyan-300 font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
    </>
  );
};

export default RegisterForm;
